<script lang="ts">
	/**
	 * Confirmation dialog shown after the user clicks Speichern on the analysis page when the analysis'
	 * declaration-determining values match one or more "Deklarationsbestimmende Parameter" fields in the lot's
	 * Materialeigenschaften. It offers to transfer those values into the matched fields.
	 *
	 * The transfer checkbox drives the textareas:
	 * - unchecked (default): each textarea shows the field's existing text unchanged and is disabled — nothing is
	 *   pasted until the user opts in.
	 * - checked: each textarea shows the combined text (existing + the worst values NOT already present, deduped
	 *   server-side); only that newly added suffix is highlighted blue and the field becomes editable.
	 * Re-checking restores the server-suggested combined text (manual edits discarded on toggle).
	 */
	import { t } from '$i18n/i18n'
	import Dialog from '$lib/atoms/Dialog.svelte'
	import Checkbox from '$lib/atoms/Checkbox.svelte'
	import Button from '$lib/atoms/Button.svelte'
	import type { DeclarationParameterEntry } from './types/declarationParameter'

	interface Props {
		/**
		 * Called when the user clicks the primary "Speichern" button.
		 * @param shouldApply true when the checkbox is on — parent should persist the analysis and then write
		 *                    {@code entries} into the material properties.
		 * @param entries     the (possibly user-edited) entries to write, in the order they were shown. Only
		 *                    meaningful when {@code shouldApply} is true.
		 */
		onConfirm: (shouldApply: boolean, entries: DeclarationParameterEntry[]) => void
	}

	let { onConfirm }: Props = $props()

	let dialog: Dialog
	/** live editable copy — its valueToAppend is bound to each textarea */
	let entries: DeclarationParameterEntry[] = $state([])
	/** server-suggested combined values, kept so checking pastes them and re-checking restores them */
	let combinedValues: string[] = $state([])
	/** backdrop mirror divs — one per entry, paint the blue highlight behind each transparent textarea */
	let backdrops: HTMLDivElement[] = $state([])
	let accepted = $state(false)
	let hasEntries = $derived(entries.length > 0)

	export function open(matchedEntries: DeclarationParameterEntry[]) {
		// clone so user edits don't leak back into the parent's preview object; start from the existing text
		// (unchecked) so nothing is pasted until the user opts in
		entries = matchedEntries.map((e) => ({ ...e, valueToAppend: e.existingValue }))
		combinedValues = matchedEntries.map((e) => e.valueToAppend)
		accepted = false
		dialog.open()
	}

	function close() {
		dialog.close()
	}

	function confirm() {
		onConfirm(accepted && hasEntries, entries)
		dialog.close()
	}

	/**
	 * Checkbox toggle: swap every textarea between the field's existing text (off) and the server-suggested
	 * combined text (on). Any manual edits are intentionally discarded on toggle — re-checking restores the
	 * original suggestion.
	 */
	function onCheckboxChange(event: CustomEvent<{ checked: boolean }>) {
		accepted = event.detail.checked
		entries = entries.map((entry, i) => ({
			...entry,
			valueToAppend: accepted ? combinedValues[i] : entry.existingValue
		}))
	}

	function escapeHtml(text: string): string {
		return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
	}

	/**
	 * Renders the field text for the backdrop mirror, wrapping the newly appended suffix in a {@code <mark>} so
	 * it shows up highlighted behind the (transparent-background) textarea. Escapes HTML — the values come from
	 * analysis/material data and must not be interpreted as markup. Only highlights while the checkbox is on and
	 * the suffix is still present at the end of the current (possibly edited) text.
	 */
	function highlightMarkup(value: string, appendedValue: string): string {
		if (accepted && appendedValue && value.endsWith(appendedValue)) {
			const head = value.slice(0, value.length - appendedValue.length)
			return escapeHtml(head) + '<mark>' + escapeHtml(appendedValue) + '</mark>'
		}
		return escapeHtml(value)
	}

	function syncScroll(event: Event, index: number) {
		const textarea = event.currentTarget as HTMLTextAreaElement
		const backdrop = backdrops[index]
		if (backdrop) {
			backdrop.scrollTop = textarea.scrollTop
			backdrop.scrollLeft = textarea.scrollLeft
		}
	}
</script>

<Dialog bind:this={dialog}>
	<h2 class="title">{$t('Analytics.declarationParameter.dialog.title')}</h2>
	<p>{$t('Analytics.declarationParameter.dialog.intro')}</p>
	{#if hasEntries}
		<div class="checkbox-row">
			<Checkbox
				id="declaration-parameter-accept"
				checked={accepted}
				on:checkboxChange={onCheckboxChange}
				label={$t('Analytics.declarationParameter.dialog.checkbox')}
			/>
		</div>
	{:else}
		<p class="no-target">{$t('Analytics.declarationParameter.dialog.noTarget')}</p>
	{/if}
	<!-- key: propertyPublicId alone is not unique — the same property definition can match under several cascades -->
	{#each entries as entry, i (entry.path.join('/'))}
		<div class="entry">
			<label class="target" for="declaration-parameter-value-{i}">{entry.targetLabel}</label>
			<div class="value-wrap">
				<!-- eslint-disable-next-line svelte/no-at-html-tags -- input is escaped in highlightMarkup -->
				<div class="value-backdrop" aria-hidden="true" bind:this={backdrops[i]}>
					{@html highlightMarkup(entries[i].valueToAppend, entry.appendedValue)}
				</div>
				<textarea
					id="declaration-parameter-value-{i}"
					class="value"
					rows="3"
					bind:value={entries[i].valueToAppend}
					disabled={!accepted}
					onscroll={(event) => syncScroll(event, i)}></textarea>
			</div>
		</div>
	{/each}
	<div class="buttons">
		<Button variant="secondary" onclick={close}>{$t('NLS.cancel')}</Button>
		<Button variant="primary" onclick={confirm}>
			{$t('Analytics.declarationParameter.dialog.confirm')}
		</Button>
	</div>
</Dialog>

<style lang="scss">
	.title {
		margin: 0 0 0.5rem;
	}

	.checkbox-row {
		display: flex;
		justify-content: flex-start;
		text-align: left;
		margin: 0.5rem 0 1rem;
	}

	.entry {
		margin-bottom: 0.75rem;
		display: flex;
		flex-direction: column;
	}

	.target {
		font-weight: 500;
		margin-bottom: 0.25rem;
	}

	.no-target {
		font-style: italic;
		color: #757575;
		margin: 0.5rem 0 1rem;
	}

	.value-wrap {
		position: relative;
		width: 100%;
	}

	/* backdrop + textarea MUST share identical box metrics so the highlight lines up with the text */
	.value-backdrop,
	.value {
		box-sizing: border-box;
		width: 100%;
		margin: 0;
		font-family: Roboto, sans-serif;
		font-size: 0.75rem;
		line-height: 1.4;
		padding: 0.5rem;
		border: 1px solid transparent;
		border-radius: 2px;
		white-space: pre-wrap;
		overflow-wrap: break-word;
	}

	.value-backdrop {
		position: absolute;
		inset: 0;
		overflow: hidden;
		color: transparent;
		pointer-events: none;

		:global(mark) {
			background: #b3d4ff;
			color: transparent;
			border-radius: 2px;
		}
	}

	.value {
		position: relative;
		resize: vertical;
		background: transparent;
		border-color: #d9d9d9;

		&:disabled {
			background-color: #f5f5f5;
			color: #757575;
			cursor: not-allowed;
		}
	}

	.buttons {
		display: flex;
		justify-content: flex-end;
		gap: 8px;
		margin-top: 1rem;
	}
</style>
